import React, { useEffect, useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { InputAdornment } from "@material-ui/core";
import LoadingButton from '@material-ui/lab/LoadingButton';
import { Alert, AlertTitle, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useLocation } from '@reach/router';
import qs from "querystringify";
import { Controller, useForm } from "react-hook-form";
import useFetch from 'use-http';
import * as yup from 'yup';

import { orderCall } from '../../landing/src/utils/metrics';
import getAppTrackingParams from "../utils/getTrackingParams";
import FieldAutocompleteLegalEntity from "./FormFieldWrappers/FieldAutocompleteLegalEntity";
import FormTextField from './FormTextField';
import IconValidField from "./IconValidField";
import PhoneMaskInput from './PhoneMaskInput';

const schema = yup.object({
  name: yup
    .string()
    .required('Укажите фамилию, имя и отчество через пробел')
    .max(256, 'Укажите фамилию, имя и отчество через пробел')
    .min(2, 'Укажите фамилию, имя и отчество через пробел'),
  inn: yup
    .object()
    .nullable()
    .required("Укажите организацию или ИП"),
  phone: yup
    .string()
    .required('Укажите номер телефона')
    .matches(/(^\+7\s\([0-9]{3}\)\s[0-9]{3}-[0-9]{2}-[0-9]{2}$)/, {
      message: 'Номер телефона должен состоять из 10 цифр',
    }),
});

const CREATE_CONSULTATION_REQUEST =
  `${process.env.GATSBY_FRONTOFFICE_API}/consultations`;

export default function ConsultationFormForSber() {
  const location = useLocation();
  const utm_source = useRef(null);
  const utm_full = useRef(null);

  const [checkedAd, setCheckedAd] = useState(false);

  useEffect(() => {
    if (location.search.includes('utm_source')) {
      let urlParam = qs.parse(location.search);

      utm_source.current = urlParam.utm_source;
      utm_full.current = location.href;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const methods = useForm({
    mode: 'onTouched',
    // criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      // name: 'Петров Иван',
      // phone: '+70902567903',
    },
    resolver: yupResolver(schema),
  });

  const { control, formState, getValues, handleSubmit, setError } = methods;

  const { post, response } = useFetch(CREATE_CONSULTATION_REQUEST, {
    cachePolicy: 'no-cache',
    retries: 3,
    retryDelay: ({ attempt }) => Math.min(
      attempt > 0
        ? 2 ** attempt * 1000
        : 1000, 30 * 1000,
    ),
    async retryOn({ error, response }) {
      // retry on any network error, or 5xx status codes
      if (error || response?.status >= 500) {
        return true;
      }
    },
    onError: ({ error }) => {
      const values = getValues();
      console.error(
        'Серверная ошибка при отправке заявки на консультацию',
        values,
        error,
      );

      if (error.name === '400') {
        Object.entries(response.data.errors).forEach(
          ([fieldName, [message]]) => setError(fieldName, { message }),
        );
      } else {
        setError('FORM_ERROR', { message: error.message });
      }
    },
  });

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'send_form_main',
          ea: 'sent_form',
          ec: 'zayavka_cons',
        });
      } else {
        console.error('dataLayer не найден');
      }

      if (window.ga) {
        window.ga('send', 'event', 'button', 'zayavka_cons', '');
      } else {
        console.error('GA не найден');
      }

      if (window) {
        orderCall();
      }
    }
  }, [formState.isSubmitSuccessful]);

  async function onSubmit(data) {
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('phone', data.phone);
    if (checkedAd) {
      formData.append('advertisement_accepted', checkedAd);
    }
    formData.append('inn', data.inn.data.inn);
    // formData.append('email', "Sber@test.ru");
    // formData.append('amount', "1000");


    // formData.append('roistat_id', window?.roistat?.visit);

    const trackingParams = getAppTrackingParams(location);
    Object.entries(trackingParams).forEach(([key, value]) => {
      if (utm_source.current === 'avito' && key === 'channel') {
        formData.append('channel', utm_source.current);
      } else if (utm_source.current === 'avito' && key === 'landing_url') {
        formData.append('landing_url', utm_full.current);
      } else {
        formData.append(key, value);
      }
    });

    try {
      await post(formData);
    } catch (error) {
      setError('FORM_ERROR', { message: error?.message });
    }
  }

  const [checked, setChecked] = useState(false);

  const onToggle = () => {
    setChecked(!checked);
  }

  const onToggleAd = () => {
    setCheckedAd(!checkedAd);
  }

  if (formState.isSubmitSuccessful) {
    return (
      <Box display="flex" flexDirection="column" p={4}>
        <Box color="success.main" my={5}>
          <Typography align="center" variant="h3">
            Спасибо за заявку. Мы свяжемся с Вами в течение одного рабочего часа с 9:00 до 18:00 по московскому времени.
          </Typography>
        </Box>
        <Box mb={3} />
      </Box>
    );
  }

  return (
    <Box
      autoCapitalize="off"
      autoCorrect="off"
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spellCheck="false"
      sx={{ mb: 1 }}
    >
      <FormTextField
        InputLabelProps={{ htmlFor: 'form-consultation-name' }}
        control={control}
        error={formState?.errors?.name}
        helperText={formState?.errors?.name?.message}
        id="form-consultation-name"
        label="Имя"
        name="name"
        placeholder="Иванов Михаил Валентинович"
        sx={{ mb: 1 }}
        variant="filled"
      />

      <Controller
        control={control}
        name="inn"
        render={({ field: { onBlur, onChange, ref, value }, fieldState: { error } }) => {
          return (
            <FieldAutocompleteLegalEntity
              defaultValue={value}
              error={error}
              helperText={(error && error?.message)}
              hideIcon={true}
              inputProps={{
                endAdornment: (
                  <>
                    {!error?.message && value && (
                      <InputAdornment
                        position="end"
                        sx={{ position: "relative", top: "-8px" }}
                      >
                        <IconValidField />
                      </InputAdornment>
                    )}
                  </>
                ),
              }}
              inputRef={ref}
              label={"Поиск компаний по ИНН"}
              onBlur={onBlur}
              onChange={onChange}
            />
          )
        }}
      />

      <FormTextField
        InputLabelProps={{ htmlFor: 'form-consultation-phone' }}
        InputProps={{ inputComponent: PhoneMaskInput }}
        control={control}
        error={formState?.errors?.phone}
        helperText={formState?.errors?.phone?.message}
        id="form-consultation-phone"
        label="Контактный телефон"
        name="phone"
        placeholder="+7 (***) ***-**-**"
        sx={{ mb: 1, marginTop: "12px" }}
        type="tel"
        variant="filled"
      />

      <br />
      <br />

      <FormControlLabel sx={{ marginBottom: '16px' }} control={<Checkbox checked={checked} onClick={onToggle} />} label={
        <>
          Я даю <a href={`${process.env.GATSBY_DOCUMENTS_PUBLIC_URL}/acceptances/%D0%A1%D0%BE%D0%B3%D0%BB%D0%B0%D1%81%D0%B8%D0%B5_%D0%BD%D0%B0_%D0%BE%D0%B1%D1%80%D0%B0%D0%B1%D0%BE%D1%82%D0%BA%D1%83_%D0%9F%D0%94_%D0%90%D1%80%D0%B5%D0%BD%D0%B7%D0%B0_%D0%9B%D0%B5%D0%BD%D0%B4%D0%B8%D0%BD%D0%B3_%D0%A1%D0%B1%D0%B5%D1%80%D0%B0.pdf`} rel="noreferrer" style={{ color: '#2484c6', textDecoration: "underline" }} target="_blank">согласие</a> на обработку своих персональных данных в соответствии с <a href={`${process.env.GATSBY_DOCUMENTS_PUBLIC_URL}/политика_обработки_персональных_данных.pdf`} rel="noopener noreferrer" target={"_blank"} style={{ color: '#2484c6', textDecoration: "underline" }}>политикой конфиденциальности</a> персональных данных Сервиса.
        </>
      } />

      <FormControlLabel control={<Checkbox checked={checkedAd} onClick={onToggleAd} />} label={
        <>
          Я даю <a href={`${process.env.GATSBY_DOCUMENTS_PUBLIC_URL}/acceptances/%D0%A1%D0%BE%D0%B3%D0%BB%D0%B0%D1%81%D0%B8%D0%B5_%D0%BD%D0%B0_%D1%80%D0%B5%D0%BA%D0%BB%D0%B0%D0%BC%D0%BD%D1%83%D1%8E_%D1%80%D0%B0%D1%81%D1%81%D1%8B%D0%BB%D0%BA%D1%83_%D0%90%D1%80%D0%B5%D0%BD%D0%B7%D0%B0.pdf`} rel="noreferrer" style={{ color: '#2484c6', textDecoration: "underline" }} target="_blank">согласие </a> на получение информационных и рекламных рассылок в соответствии с офертой Сервиса
        </>
      } />

      {formState.errors['FORM_ERROR'] && (
        <Alert severity="error">
          <AlertTitle>
            <Box mt={1}>Произошла ошибка при отправке заявки</Box>
          </AlertTitle>
          <Typography variant="body2">
            Мы уже знаем об этом и скоро исправим
          </Typography>
        </Alert>
      )}
      <Box my={4}>
        <LoadingButton
          color="primary"
          disabled={!!formState.errors['FORM_ERROR'] || !checked}
          fullWidth
          loading={formState.isSubmitting}
          size="large"
          type="submit"
          variant="contained"
        >
          Отправить
        </LoadingButton>
      </Box>
    </Box>
  );
}
